import { createTheme } from '@mui/material'
import { createBreakpoints } from '@mui/system'

// TODO: Figure out how to load this from an external types file
declare module '@mui/material/styles' {
  interface TypographyVariants {
    title: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    title?: React.CSSProperties
  }

  interface ButtonPropsVariants {
    selected?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    selected: true
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    mdl: true
    lg: true
    xl: true
  }
}

export enum BrandColor {
  BLACK = '#000000',
  WHITE = '#ede8e3',
  GREEN = '#00b74f',
  PINK = '#f57eb6',
  BLUE = '#0057b7',
}

export enum BrandFontFamily {
  REGULAR = "'Gotham Book', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue'",
  BOLD = "'Rebel Sans Regular'"
}

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    mdl: 1000,
    lg: 1200,
    xl: 1536,
  },
})

export const theme = createTheme({
  breakpoints,

  typography: {
    fontFamily: BrandFontFamily.REGULAR,
    ...(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].reduce((acc, header) => {
      return {
        ...acc,
        [header]: {
          fontFamily: BrandFontFamily.BOLD,
          textTransform: 'uppercase',
        },
      }
    }, {})),

    // Custom
    title: {
      fontFamily: BrandFontFamily.BOLD,
      textTransform: 'uppercase',
    },
  },
  components: {
    // TYPOGRAPHY
    MuiTypography: {
      styleOverrides: {
        button: {
          fontFamily: BrandFontFamily.BOLD,
          fontSize: '1.1rem',
        },
      },
    },

    // NAVIGATION
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'transparent',
        },
      },
    },

    // LINK
    MuiLink: {
      styleOverrides: {
        root: {
          color: BrandColor.BLACK,
          textDecorationColor: BrandColor.BLACK,
        },
      },
    },

    // FORM

    // Inputs
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: BrandColor.BLACK,
          '&.Mui-focused': {
            color: BrandColor.BLUE,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BrandColor.BLUE,
          },
        },
        notchedOutline: {
          borderColor: BrandColor.BLACK,
          color: BrandColor.BLACK,
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: BrandColor.BLUE,
          },
        },
      },
    },

    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          color: BrandColor.BLACK,
          fontFamily: BrandFontFamily.BOLD,
          fontSize: '1.1rem',
          padding: '0.75rem 1rem',
          textDecoration: 'underline',

          '&:hover': {
            boxShadow: 'none',
            textDecoration: 'underline',
          },

          '&.MuiButton-containedPrimary': {
            backgroundColor: BrandColor.PINK,
            color: BrandColor.BLACK,
            textDecoration: 'none',
            border: `1px solid ${BrandColor.PINK}`,
          },

          '&:hover .MuiButton-containedPrimary': {
            textDecoration: 'none',
          },

          '&.MuiButton-outlinedPrimary': {
            color: BrandColor.BLACK,
            textDecoration: 'none',
            border: `1px solid ${BrandColor.BLACK}`,
          },
        },
      },
      variants: [
        {
          props: { variant: 'selected' },
          style: {
            background: BrandColor.BLACK,
            color: BrandColor.WHITE,
            textDecoration: 'none',

            '&:hover': {
              background: BrandColor.BLACK,
              color: BrandColor.WHITE,
              textDecoration: 'none',
            },
          },
        },
      ],
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: BrandColor.BLACK,
        },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: BrandColor.WHITE,
          display: 'inline-flex',
          flexGrow: 1,
          flexShrink: 1,
          margin: 0,
          padding: 0,
          maxWidth: 'none',
          minWidth: 24,
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        label: {
          fontFamily: BrandFontFamily.BOLD,
          textTransform: 'uppercase',
          display: 'inline-flex',
          boxSizing: 'border-box',
          flexGrow: 1,
          textAlign: 'center',
          [breakpoints.only('md')]: {
            fontSize: 15,
          },
          [breakpoints.only('sm')]: {
            fontSize: 12,
          },
          [breakpoints.only('xs')]: {
            fontSize: 10,
          },
        },
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          position: 'fixed',
          bottom: 0,
          width: '100%',
          backgroundColor: BrandColor.BLACK,
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0rem 1rem',
          [breakpoints.up('md')]: {
            display: 'none',
          },
          [breakpoints.down('md')]: {
            display: 'flex',
          },
          flexGrow: 1,
          borderTop: '1px solid black',
        },
      },
    },

    // Alerts
    MuiSnackbar: {
      styleOverrides: {
        root: {
          backgroundColor: BrandColor.BLACK,
          color: BrandColor.WHITE,

          '.MuiButtonBase-root': {
            fontSize: 10,
            padding: '0.5rem 1rem',
            color: BrandColor.WHITE,
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: BrandColor.BLACK,
          color: BrandColor.WHITE,

          '.MuiButtonBase-root': {
            fontSize: 10,
            padding: '0.5rem 1rem',
            color: BrandColor.WHITE,
          },
        },
      },
    },

    // Dialogs
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: BrandColor.WHITE,
        },
      },
    },

    MuiMobileStepper: {
      styleOverrides: {
        root: {
          backgroundColor: BrandColor.WHITE,
          justifyContent: 'center',

          '.MuiLinearProgress-root': {
            width: '100%',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        icon: {
          color: 'inherit',
        },
      },
    },
  },
})
